import { useState } from 'react';
import { ChatBubbleLeftIcon, ShareIcon, ArrowPathRoundedSquareIcon } from '@heroicons/react/24/outline';
import type { SocialPost } from './types';
import type { ClubTheme } from './themes';
import ClubBadge from './ClubBadge';
import ReactionBar from './ReactionBar';
import CommentThread from './CommentThread';
import ShareMenu from './ShareMenu';
import MediaAttachments from './MediaAttachments';
import QuotedPostEmbed from './QuotedPostEmbed';
import PostLabels from './PostLabels';
import AnnotatableText from './AnnotatableText';
import LinkButton from './LinkButton';
import { useSharePost, useAnnotations, useCreateAnnotation, useFactCheck, useSpawnAnnotationDiscussion } from './queries';
import { useAuth } from '@/lib/auth-context';

interface PostCardProps {
  post: SocialPost;
  theme: ClubTheme;
  onRefresh?: () => void;
  onQuote?: (post: SocialPost) => void;
  onNavigateToPost?: (postId: string) => void;
  onViewMember?: (userId: string) => void;
}

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(dateStr).toLocaleDateString();
}

const FLAIR_ICONS: Record<string, string> = {
  milestone: '🏅',
  highlight: '✦',
  announcement: '📢',
  post: '✦',
};

export default function PostCard({ post, theme, onRefresh, onQuote, onNavigateToPost, onViewMember }: PostCardProps) {
  const [showComments, setShowComments] = useState(false);
  const [factCheckStatus, setFactCheckStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [factCheckMessage, setFactCheckMessage] = useState('');
  const { user } = useAuth();
  const share = useSharePost(post.club_id, post.id);
  const { data: annotations = [] } = useAnnotations(post.club_id, post.id);
  const createAnnotation = useCreateAnnotation(post.club_id, post.id);
  const factCheck = useFactCheck(post.club_id);
  const spawnDiscussion = useSpawnAnnotationDiscussion(post.club_id);

  const isSystem = post.author_type === 'system';
  const isOwn = user?.id === post.author_id;
  const canShare = isOwn && !post.shared_to_community && !isSystem;
  const authorName = post.author?.full_name || 'Unknown';
  const authorRole = post.author?.role || 'member';
  const authorHandle = post.author?.handle;
  const ROLE_ROUTE_PREFIX: Record<string, string> = { teacher: '/t/', sponsor: '/s/', parent: '/u/', student: '/u/', admin: '/u/', superadmin: '/u/' };
  const authorProfileUrl = authorHandle ? `${ROLE_ROUTE_PREFIX[authorRole] || '/u/'}${authorHandle}` : undefined;

  return (
    <article
      id={`post-${post.id}`}
      className="rounded-2xl p-4 transition-shadow hover:shadow-md"
      style={{
        backgroundColor: isSystem ? `${theme.colors.primary}10` : theme.colors.surface,
        border: post.is_pinned
          ? `2px solid ${theme.colors.primary}`
          : isSystem
          ? `1px solid ${theme.colors.primary}30`
          : `1px solid ${theme.colors.muted}20`,
      }}
    >
      {/* Pinned indicator */}
      {post.is_pinned && (
        <div className="mb-2 text-[10px] font-bold uppercase tracking-widest" style={{ color: theme.colors.primary }}>
          Pinned
        </div>
      )}

      {/* System flair badge */}
      {isSystem && post.post_type !== 'post' && (
        <div
          className="mb-2 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
          style={{ backgroundColor: `${theme.colors.primary}20`, color: theme.colors.primary }}
        >
          <span>{FLAIR_ICONS[post.post_type] || '✦'}</span>
          <span>{post.post_type}</span>
        </div>
      )}

      {/* Post labels */}
      {post.labels?.length > 0 && <PostLabels labels={post.labels} theme={theme} />}

      {/* Author row */}
      <div className="flex items-center gap-3">
        {isSystem ? (
          <div
            className="flex h-10 w-10 items-center justify-center rounded-full text-lg"
            style={{
              background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent})`,
              color: theme.colors.bg,
            }}
          >
            {FLAIR_ICONS[post.post_type] || '✦'}
          </div>
        ) : post.author?.avatar_url ? (
          <img
            src={post.author.avatar_url}
            alt=""
            className="h-9 w-9 rounded-full object-cover"
          />
        ) : (
          <div
            className="flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold"
            style={{ backgroundColor: theme.colors.primary, color: theme.colors.bg }}
          >
            {(authorName[0] || '?').toUpperCase()}
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {!isSystem && onViewMember && post.author_id ? (
              <button
                type="button"
                onClick={() => onViewMember(post.author_id)}
                className="text-sm font-semibold truncate hover:underline text-left"
                style={{ color: theme.colors.text }}
              >
                {authorName}
              </button>
            ) : !isSystem && authorProfileUrl ? (
              <a
                href={authorProfileUrl}
                className="text-sm font-semibold truncate hover:underline"
                style={{ color: theme.colors.text }}
              >
                {authorName}
              </a>
            ) : (
              <span
                className="text-sm font-semibold truncate"
                style={{ color: isSystem ? theme.colors.primary : theme.colors.text }}
              >
                {isSystem ? theme.name : authorName}
              </span>
            )}
            {!isSystem && <ClubBadge role={authorRole} theme={theme} />}
          </div>
          <div className="flex items-center gap-2 text-[11px]" style={{ color: theme.colors.muted }}>
            {post.channel && <span>{post.channel.name}</span>}
            {(post.metadata as any)?.circle_only && (
              <span style={{ color: theme.colors.primary }}>🔗 Circle</span>
            )}
            <span>{timeAgo(post.created_at)}</span>
          </div>
        </div>

        {/* Link button — only for non-system, non-own posts */}
        {!isSystem && !isOwn && post.author_id && (
          <LinkButton clubId={post.club_id} targetId={post.author_id} theme={theme} />
        )}
      </div>

      {/* Body — annotatable for inline fact-check, notes, questions */}
      {post.body && (
        <AnnotatableText
          text={post.body}
          annotations={annotations}
          theme={theme}
          isSystem={isSystem}
          clubId={post.club_id}
          postId={post.id}
          onAnnotate={(sel) => {
            // Determine type from the last toolbar button clicked
            // The toolbar buttons in AnnotatableText fire with type info
            createAnnotation.mutate({
              start_offset: sel.start,
              end_offset: sel.end,
              highlighted_text: sel.text,
              annotation_type: sel.type || 'note',
              body: '',
            });
          }}
          onFactCheck={(sel) => {
            setFactCheckStatus('loading');
            setFactCheckMessage('Verifying claims with AI...');
            factCheck.mutate(
              {
                post_id: post.id,
                highlighted_text: sel.text,
                start_offset: sel.start,
                end_offset: sel.end,
              },
              {
                onSuccess: (res: any) => {
                  const count = res?.data?.annotations?.length ?? 0;
                  const msg = res?.data?.message;
                  setFactCheckStatus('done');
                  setFactCheckMessage(
                    msg || (count > 0 ? `${count} claim${count > 1 ? 's' : ''} verified` : 'No verifiable claims found')
                  );
                  setTimeout(() => setFactCheckStatus('idle'), 5000);
                },
                onError: (err: any) => {
                  setFactCheckStatus('error');
                  setFactCheckMessage(err?.message || 'Fact-check failed');
                  setTimeout(() => setFactCheckStatus('idle'), 5000);
                },
              }
            );
          }}
          onStartDiscussion={(annotationId) => {
            spawnDiscussion.mutate(
              { annotationId },
              { onSuccess: () => onRefresh?.() }
            );
          }}
        />
      )}

      {/* Fact-check status banner */}
      {factCheckStatus !== 'idle' && (
        <div
          className="mt-2 flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium"
          style={{
            backgroundColor:
              factCheckStatus === 'loading' ? `${theme.colors.primary}15`
              : factCheckStatus === 'error' ? '#EF444415'
              : '#10B98115',
            color:
              factCheckStatus === 'loading' ? theme.colors.primary
              : factCheckStatus === 'error' ? '#EF4444'
              : '#10B981',
          }}
        >
          {factCheckStatus === 'loading' && (
            <div
              className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-t-transparent"
              style={{ borderColor: 'currentColor', borderTopColor: 'transparent' }}
            />
          )}
          {factCheckStatus === 'done' && <span>🛡️</span>}
          {factCheckStatus === 'error' && <span>⚠️</span>}
          <span>{factCheckMessage}</span>
        </div>
      )}

      {/* Quoted post — visual thread indicator (clickable) */}
      {post.quoted_post && (
        <div className="mt-2">
          <div
            className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider"
            style={{ color: theme.colors.accent }}
          >
            <ArrowPathRoundedSquareIcon className="h-3 w-3" />
            <span>
              Quoted {post.quoted_post.author?.full_name || 'a post'}
            </span>
          </div>
          <QuotedPostEmbed
            post={post.quoted_post}
            theme={theme}
            onNavigate={(postId) => onNavigateToPost?.(postId)}
          />
        </div>
      )}

      {/* Media — video, audio, images, PDFs, docs */}
      <MediaAttachments media={post.media_urls} theme={theme} />

      {/* Reactions + Actions */}
      <div className="mt-3 flex items-center justify-between">
        <ReactionBar
          clubId={post.club_id}
          postId={post.id}
          reactions={post.reactions}
          theme={theme}
          onReacted={onRefresh}
        />

        <div className="flex items-center gap-2">
          {/* Share to community */}
          {canShare && (
            <button
              type="button"
              onClick={() => share.mutate(undefined, { onSuccess: () => onRefresh?.() })}
              disabled={share.isPending}
              title="Share to community"
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs transition-colors hover:opacity-80"
              style={{ color: theme.colors.muted }}
            >
              <ShareIcon className="h-3.5 w-3.5" />
              <span>Share</span>
            </button>
          )}
          {post.shared_to_community && (
            <span
              className="text-[10px] font-medium"
              style={{ color: theme.colors.accent }}
            >
              Shared
            </span>
          )}

          {/* Quote post */}
          {!isSystem && onQuote && (
            <button
              type="button"
              onClick={() => onQuote(post)}
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs transition-colors hover:opacity-80"
              style={{ color: theme.colors.muted }}
              title="Quote this post"
            >
              <ArrowPathRoundedSquareIcon className="h-3.5 w-3.5" />
              {(post.quote_count ?? 0) > 0 && <span>{post.quote_count}</span>}
            </button>
          )}

          {/* Share externally (X, FB, LinkedIn, etc.) */}
          <ShareMenu
            text={post.body || ''}
            variant="themed"
            colors={theme.colors}
          />

          {/* Comments toggle */}
          <button
            type="button"
            onClick={() => setShowComments(!showComments)}
            className="inline-flex items-center gap-1 text-xs transition-colors hover:opacity-80"
            style={{ color: theme.colors.muted }}
          >
            <ChatBubbleLeftIcon className="h-4 w-4" />
            {post.comment_count > 0 && <span>{post.comment_count}</span>}
          </button>
        </div>
      </div>

      {/* Comment thread */}
      {showComments && (
        <div className="mt-3 border-t pt-3" style={{ borderColor: `${theme.colors.muted}20` }}>
          <CommentThread
            clubId={post.club_id}
            postId={post.id}
            theme={theme}
          />
        </div>
      )}
    </article>
  );
}
